import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, XIcon } from 'lucide-react';
import type { Notification } from '@mockd/shared';
import { useNotifications } from '../../hooks';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

export const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, setRead, markAllRead, dismiss } = useNotifications();
  const navigate = useNavigate();

  const closeMenu = () => (document.activeElement as HTMLElement)?.blur();

  const openNotification = (notification: Notification) => {
    closeMenu();
    if (!notification.readAt) setRead(notification.id, true);
    if (notification.link) navigate(notification.link);
  };

  return (
    <div className="dropdown dropdown-end">
      <div
        tabIndex={0}
        role="button"
        className="btn btn-ghost btn-circle"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
      >
        <div className="indicator">
          <BellIcon className="size-5" />
          {unreadCount > 0 && (
            <span className="badge badge-xs badge-primary indicator-item">{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </div>
      </div>
      <div
        tabIndex={-1}
        className="dropdown-content bg-base-100 border border-base-300 rounded-box z-1 mt-3 w-80 max-w-[calc(100vw-2rem)]"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-base-300">
          <span className="font-semibold text-sm">Notifications</span>
          {unreadCount > 0 && (
            <button type="button" className="btn btn-ghost btn-xs" onClick={markAllRead}>
              Mark all read
            </button>
          )}
        </div>
        {notifications.length === 0 ? (
          <p className="px-4 py-6 text-sm text-center text-base-content/60">No notifications</p>
        ) : (
          <ul className="max-h-96 overflow-y-auto divide-y divide-base-300">
            {notifications.map((notification) => (
              <li key={notification.id} className="flex gap-2 px-4 py-3 hover:bg-base-200">
                <button type="button" className="flex-1 min-w-0 text-left" onClick={() => openNotification(notification)}>
                  <p className={`text-sm ${notification.readAt ? 'text-base-content/70' : 'font-medium'}`}>{notification.title}</p>
                  <p className="text-xs text-base-content/60 mt-0.5">{notification.body}</p>
                  <p className="text-xs text-base-content/70 mt-1">{formatRelativeTime(notification.createdAt)}</p>
                </button>
                <div className="flex flex-col items-center gap-1">
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-circle"
                    aria-label={notification.readAt ? 'Mark as unread' : 'Mark as read'}
                    title={notification.readAt ? 'Mark as unread' : 'Mark as read'}
                    onClick={() => setRead(notification.id, !notification.readAt)}
                  >
                    <span className={`w-2 h-2 rounded-full ${notification.readAt ? 'border border-base-content/40' : 'bg-primary'}`} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-circle"
                    aria-label="Dismiss"
                    title="Dismiss"
                    onClick={() => dismiss(notification.id)}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
